<template>
	<div class="org-settings-main">
		<div class="header">
			<h1 v-t="'settings_title'" />

			<Button
				primary
				class="save-button"
				@click="updateSettings"
				:disabled="!changed"
				:loading="isUpdating"
			>
				{{ isUpdating ? $t('meetings_record_saving') : $t('common_button_save') }}
			</Button>
		</div>

		<div class="content">
            <div
				v-if="isUpdating"
				class="settings-content-updating"
				:class="{
					'settings-content-updating-banner': store.showAnnouncementsBanner,
				}"
			></div>
            <div class="grouping-label" v-t="'admin_settings_features_label'"/>
            <div class="features">
                <ToggleBox
                    :title="
                        $t('admin_settings_userprofiles_pronouns')
                    "
                    :description="$t('admin_settings_userprofiles_pronouns_description')"
                    :toggleChecked="pronounsEnabled"
                    @checkedEvent="pronounsCheckedEvent"
                    :spinner="false"
                    data-cy="toggle-pronouns"
                />
                <ToggleBox
                    :title="
                        $t('admin_settings_userprofiles_title')
                    "
                    :description="$t('admin_settings_userprofiles_title_description')"
                    :toggleChecked="titleEnabled"
                    @checkedEvent="titleCheckedEvent"
                    :spinner="false"
                    data-cy="toggle-title"
                />
            </div>
            <div class="grouping-label" v-t="'admin_settings_options_label'"/>
            <div class="options">
                <div class="giphy-box-wrapper">
                    <div class="giphybox">
                        <div>
                            <div class="title">Giphy Rating</div>
                            <div class="description">Set your preferred Giphy Rating</div>
                        </div>
                        <Dropdown
                            class="giphy-rating"
                            :options="options"
                            v-model="giphyRating"
                            ref="ratingDropdown"
                            optionsClass="org-giphy-rating"
                            @change="giphyRatingChangeEvent"
                        >
                            <template #selected="{ option }">
                                {{ $t(option.label) }}
                            </template>

                            <template v-slot="{ option }">
                                {{ $t(option.label) }}
                            </template>
                        </Dropdown>
                    </div>
                </div>
            </div>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Button, useAlert, Dropdown } from '@/components/common'
import ToggleBox from '@/components/ToggleBox'
import { updateOrg } from '@/api/organization'
import store, { getOrganization } from '@/store'
import { useI18n } from 'vue-i18n'
import { getErrorText } from '@/helperMethods'

const alert = useAlert()
const { t } = useI18n()
const router = useRouter()

const changed = ref(false)
const isUpdating = ref(false)
const pronounsEnabled = ref(false)
const titleEnabled = ref(false)
const giphyRating = ref('PG-13')
const options = [
	{ id: 'G', label: 'G' },
	{ id: 'PG', label: 'PG' },
	{ id: 'PG-13', label: 'PG-13' },
    { id: 'R', label: 'R'}
]

const updateSettings = async () => {
    isUpdating.value = true
    const payload = {
        user_title_enabled: titleEnabled.value,
        user_pronouns_enabled: pronounsEnabled.value,
        giphy_rating: giphyRating.value
    }
    try {
        await updateOrg(payload)
        changed.value = false
        alert.success(
            t('admin_people_changes_saved_confirmation_label')
        )
    } catch (error) {
        console.error(e)
        alert.error(t(getErrorText(e.response.data.error)))
    } finally {
        isUpdating.value = false
    }
}

const titleCheckedEvent = (bool) => {
    if (bool === titleEnabled.value) return
    titleEnabled.value = bool
    changed.value = true
}

const pronounsCheckedEvent = (bool) => {
    if (bool === pronounsEnabled.value) return
    pronounsEnabled.value = bool
    changed.value = true
}

const giphyRatingChangeEvent = () => {
    changed.value = true
}

const handleBeforeUnload = (event) => {
    if (changed.value) {
        event.preventDefault()
        event.returnValue = ''
        return ''
    }
}

onMounted(async () => {
    const organization = await getOrganization()
    pronounsEnabled.value = organization.user_pronouns_enabled || false
    titleEnabled.value = organization.user_title_enabled || false
    giphyRating.value = organization.giphy_rating || 'PG-13'
    window.addEventListener('beforeunload', handleBeforeUnload)
    router.beforeEach(beforeRouteLeave)
})

onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    router.beforeEach(() => {}) 
})

const beforeRouteLeave = (to, from, next) => {
    if (changed.value) {
        const answer = window.confirm('You have unsaved changes. Do you really want to leave?')
        if (answer) {
            changed.value = false
            router.beforeEach(() => {}) 
            next()
        } else {
            next(false)
        }
    } else {
        next()
    }
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.org-settings-main {
    .save-button {
        position: absolute;
        top: 32px;
        right: 32px;
    }

    .header {
        display: flex;
        align-items: center;
        height: 105px;
        padding-left: 32px;
        position: relative;
        h1 {
            font-family: Avenir-Heavy;
            font-size: 36px;
            margin: 0;
            color: var(--neutral-800);
        }
        border-bottom: 1px solid var(--neutral-200);
    }

    .content {
        .settings-content-updating {
            background-color: rgba(0, 0, 0, 0.25);
            position: absolute;
            z-index: 1;
            top: 104px;
            left: 64px;
            right: 0;
            bottom: 0;
            pointer-events: none;
        }

        .settings-content-updating-banner {
            top: 190px;
        }

        padding: 32px;
        .grouping-label {
            font-family: Avenir-Heavy;
            font-size: 26px;
            color: var(--neutral-800); 
        }
        .features, .options {
            display: flex;
            gap: 26px;
            padding-top: 26px;
            padding-bottom: 36px;
            > * {
                flex-grow: 1;
            }
        }
    }

    .giphy-box-wrapper {
        position: relative;
        max-width: 50%;
        .giphybox {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 15px 0 25px;
            border-radius: 4px;
            border: solid 1px var(--neutral-40040);
            .title {
                font-family: Avenir-Medium;
                font-size: 16px;
            }
            .description {
                font-family: Avenir-Medium;
                font-size: 14px;
                color: var(--neutral-500);
                margin-top: 10px;
                margin-right: 5px;
                padding-bottom: 4px;
            }
            .dropdown {
                width: 125px;
            }
        }
    }
}

</style>